// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file RadioListItem.tsx
 * @input Uses React use, useId, RadioListContext
 * @output Exports RadioListItem component, RadioListItemProps
 * @position Core implementation; consumed by index.ts, tested by RadioList.test.tsx
 *
 * Composes Item for the shared start content + label + description + end content layout.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/RadioList/RadioList.doc.mjs
 * - /packages/core/src/RadioList/RadioList.test.tsx
 * - /packages/core/src/RadioList/index.ts
 * - /apps/storybook/stories/RadioList.stories.tsx
 * - /packages/cli/templates/blocks/components/RadioList/ (showcase blocks)
 */
import React, { use, useId } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, durationVars, easeVars, borderVars } from "../theme/tokens.stylex.js";
import { RadioListContext } from "./RadioList.js";
import { mergeProps } from "../utils/index.js";
import "./radio.markers.stylex.js";
import { radioScope } from "./radio.markers.stylex.js";
import { Item } from "../Item/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  container: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    $$css: true
  },
  radioWrapper: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kHBbk8: "xc8icb0",
    $$css: true
  },
  input: {
    kVAEAm: "x10l6tqk",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    kSiTet: "xg01cxk",
    kkrTdU: "x1ypdohk",
    kY2c9j: "x1vjfegm",
    $$css: true
  },
  inputDisabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  radio: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kaIpWk: "x16rqkct",
    k1ekBW: "xts7igz",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kB7OPa: "x9f619",
    $$css: true
  },
  radioUnchecked: {
    kVAM5u: "xvy26l8 x70talc",
    kWkggS: "x10xzikg xy1ay1n",
    $$css: true
  },
  radioChecked: {
    kVAM5u: "xad5do xaq2ahg",
    kWkggS: "x1ewilqj xwnx32n",
    $$css: true
  },
  radioWrapperFocus: {
    kI3sdo: "x1a2a7pz x1irc7jg",
    kInvED: "x1wfwxd8 xdjuwb3",
    kaIpWk: "x16rqkct",
    $$css: true
  },
  radioDisabled: {
    kSiTet: "xbyyjgo",
    kVAM5u: "x14i3s5s",
    $$css: true
  },
  radioDisabledUnchecked: {
    kWkggS: "xwmxj5m",
    $$css: true
  },
  innerDot: {
    kaIpWk: "x16rqkct",
    kWkggS: "x1azo05",
    $$css: true
  }
};
const wrapperSizeStyles = {
  sm: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
const radioSizeStyles = {
  sm: {
    kzqmXN: "x1xp8n7a",
    kZKoxP: "xmix8c7",
    $$css: true
  },
  md: {
    kzqmXN: "x17z2i9w",
    kZKoxP: "x17rw0jw",
    $$css: true
  }
};
const dotSizeStyles = {
  sm: {
    kzqmXN: "x1xc55vz",
    kZKoxP: "xdk7pt",
    $$css: true
  },
  md: {
    kzqmXN: "x1fsd2vl",
    kZKoxP: "x170jfvy",
    $$css: true
  }
};
const embeddedStyles = {
  root: {
    k8WAf4: "xt970qd",
    kg3NbH: "xnjsko4",
    kaIpWk: "x2u8bby",
    kUk6DE: "x98rzlu",
    k7Eaqz: "xeuugli",
    $$css: true
  }
};
/**
 * An individual radio item within an RadioList.
 *
 * @example
 * ```
 * <RadioListItem label="Email" value="email" />
 * <RadioListItem
 *   label="SMS"
 *   value="sms"
 *   description="Standard messaging rates apply"
 * />
 * ```
 */
export function RadioListItem({
  ref,
  label,
  value,
  description,
  isDisabled: isItemDisabled = false,
  startContent,
  endContent,
  xstyle,
  className,
  style,
  ...rest
}) {
  const context = use(RadioListContext);
  if (!context) {
    throw new Error('RadioListItem must be used within an RadioList');
  }
  const id = useId();
  const descriptionID = useId();
  const isDisabled = context.isDisabled || isItemDisabled;
  // When the whole group is disabled with a disabledMessage, radios stay
  // focusable via aria-disabled (instead of native `disabled`) so the group's
  // reason tooltip is keyboard-discoverable. Per-item disabling is unaffected
  // and always uses the native disabled attribute.
  const keepsFocusableForMessage = context.hasDisabledMessage && !isItemDisabled;
  const isChecked = context.value === value;
  const size = context.size;
  const radioCircle = /*#__PURE__*/_jsxs("div", {
    ...stylex.props(styles.radioWrapper, wrapperSizeStyles[size], !isDisabled && styles.radioWrapperFocus),
    children: [/*#__PURE__*/_jsx("input", {
      id: id,
      type: "radio",
      name: context.name,
      value: value,
      checked: isChecked,
      disabled: isDisabled && !keepsFocusableForMessage,
      "aria-disabled": keepsFocusableForMessage ? 'true' : undefined
      // A focusable-disabled radio is not natively disabled, so detach it
      // from the form instead: it keeps its name (grouping) but is excluded
      // from submission, matching a natively disabled control.
      ,
      form: keepsFocusableForMessage ? '' : undefined,
      required: context.isRequired,
      onChange: () => {
        if (isDisabled) {
          return;
        }
        context.onChange(value);
      },
      "aria-describedby": description ? descriptionID : undefined,
      ...stylex.props(styles.input, wrapperSizeStyles[size], isDisabled && styles.inputDisabled)
    }), /*#__PURE__*/_jsx("div", {
      "aria-hidden": "true",
      ...mergeProps(themeProps('radio', {
        size,
        checked: isChecked ? 'checked' : null,
        disabled: isDisabled ? 'disabled' : null
      }), stylex.props(styles.radio, radioSizeStyles[size], isChecked ? styles.radioChecked : styles.radioUnchecked, isDisabled && styles.radioDisabled, isDisabled && !isChecked && styles.radioDisabledUnchecked)),
      children: isChecked && /*#__PURE__*/_jsx("div", {
        ...mergeProps(themeProps('radio-dot', {
          size
        }), stylex.props(styles.innerDot, dotSizeStyles[size]))
      })
    })]
  });
  const mediaContent = startContent != null ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [radioCircle, startContent]
  }) : radioCircle;
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    ...mergeProps(themeProps('radio-list-item'), stylex.props(styles.container, !isDisabled && radioScope, xstyle), className, style),
    ...rest,
    children: /*#__PURE__*/_jsx(Item, {
      startContent: mediaContent,
      label: /*#__PURE__*/_jsx("label", {
        htmlFor: id,
        ...{
          0: {},
          1: {
            className: "xnbbluu x1h6gzvc"
          }
        }[!!isDisabled << 0],
        children: label
      }),
      description: description != null ? /*#__PURE__*/_jsx("span", {
        id: descriptionID,
        children: description
      }) : undefined,
      endContent: endContent,
      xstyle: embeddedStyles.root
    })
  });
}
RadioListItem.displayName = 'RadioListItem';